import { z } from 'zod';
import { GIFT_CARD_KINDS, GIFT_CARD_STATUSES, GIFT_CARD_TXN_KINDS, STORE_CREDIT_TXN_KINDS, } from '../constants/gift-cards.js';
import { emailSchema, paginatedSchema, uuidSchema } from './common.js';
/** What a customer fills in when buying a digital card for someone. */
export const giftCardRecipientSchema = z.object({
    recipientEmail: emailSchema,
    recipientName: z.string().trim().max(100).nullable().default(null),
    message: z.string().trim().max(500).nullable().default(null),
});
export const giftCardSchema = z.object({
    id: uuidSchema,
    codeLast4: z.string(),
    kind: z.enum(GIFT_CARD_KINDS),
    status: z.enum(GIFT_CARD_STATUSES),
    initialCents: z.number().int(),
    balanceCents: z.number().int(),
    purchaseOrderId: uuidSchema.nullable(),
    purchaseOrderNumber: z.string().nullable(),
    purchaserName: z.string().nullable(),
    recipientEmail: z.string().nullable(),
    recipientName: z.string().nullable(),
    issuedReason: z.string().nullable(),
    /** True while the full code can still be revealed or re-sent. */
    codeAvailable: z.boolean(),
    deliveredAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const giftCardTransactionSchema = z.object({
    id: uuidSchema,
    kind: z.enum(GIFT_CARD_TXN_KINDS),
    amountCents: z.number().int(),
    balanceAfterCents: z.number().int(),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const giftCardListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    status: z.enum(GIFT_CARD_STATUSES).optional(),
    kind: z.enum(GIFT_CARD_KINDS).optional(),
});
export const giftCardListSchema = paginatedSchema(giftCardSchema);
/** Staff issuing a card outside a sale — a make-good, a promotion, a comp. */
export const issueGiftCardSchema = z.object({
    amountCents: z.number().int().min(1).max(100_000_00),
    kind: z.enum(GIFT_CARD_KINDS).default('digital'),
    recipientEmail: emailSchema.nullable().default(null),
    recipientName: z.string().trim().max(100).nullable().default(null),
    reason: z.string().trim().min(3).max(500),
});
/** The one place the full code is ever shown: the response to issuing it. */
export const issuedGiftCardSchema = z.object({
    card: giftCardSchema,
    code: z.string(),
});
export const voidGiftCardSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const giftCardCodeSchema = z.object({
    code: z.string().trim().min(1).max(64),
});
export const revealedGiftCardCodeSchema = z.object({ code: z.string() });
/** A card looked up by code: enough to show a balance, never the full code. */
export const giftCardBalanceSchema = z.object({
    codeLast4: z.string(),
    balanceCents: z.number().int(),
});
export const storeCreditTransactionSchema = z.object({
    id: uuidSchema,
    kind: z.enum(STORE_CREDIT_TXN_KINDS),
    amountCents: z.number().int(),
    balanceAfterCents: z.number().int(),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const storeCreditSummarySchema = z.object({
    balanceCents: z.number().int(),
    transactions: z.array(storeCreditTransactionSchema),
});
/** Signed: positive adds credit, negative takes it away. */
export const adjustStoreCreditSchema = z.object({
    amountCents: z
        .number()
        .int()
        .refine((v) => v !== 0, 'Enter an amount')
        .refine((v) => Math.abs(v) <= 100_000_00, 'Amount exceeds the maximum allowed'),
    reason: z.string().trim().min(3).max(500),
});
/**
 * Stored value spent on an order. Resolved in-process into pending `payments`
 * rows — never accepted by the order-transition endpoint.
 */
export const creditTenderInputSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('gift_card'),
        code: z.string().trim().min(1).max(64),
        /** Omitted = as much of the card as the order can use. */
        amountCents: z.number().int().min(1).optional(),
    }),
    z.object({
        type: z.literal('store_credit'),
        amountCents: z.number().int().min(1).optional(),
    }),
]);
//# sourceMappingURL=gift-card.js.map